import { useCallback, useEffect, useState } from "react";
import { getMedias } from "../services/Media";
import { MediaItem } from "../models/Media";
import { CameraItem } from "../models/Camera";
import { useSelectedMediaContext } from "../context/MediaContext";
import { Card, Col, Form, ListGroup, Row } from "react-bootstrap";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import IdentifiedSpecies from "./IdentifiedSpecies";
import { useSelectedIdentifiedObjectContext } from "../context/IdentifiedObjectContext";
import "react-datepicker/dist/react-datepicker.css";
import { Spinner } from "react-bootstrap";

initializeIcons();

interface MediaProps {
  selectedCameras: CameraItem[];
  groupId?: number;
}

const PAGE_SIZE = 8;

const Media: React.FC<MediaProps> = ({
  selectedCameras,
  groupId,
}) => {
  const { setSelectedMedia } = useSelectedMediaContext();
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [selectedDate, _setSelectedDate] = useState<Date | null>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [specificDate, _setSpecificDate] = useState<string>("");
  const { selectedIdentifiedObjects } = useSelectedIdentifiedObjectContext();
  const [selectedItemId, setSelectedItemId] = useState<number>(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [previousCursor, setPreviousCursor] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);

  // Helper function to format date to "YYYY-MM-DD"
  const formatDate = (date: string | Date | null) => {
    return date ? new Date(date).toISOString().split("T")[0] : "";
  };

  const loadMedia = useCallback(() => {
    if (isLoading) return;

    setIsLoading(true);

    const selectedCameraIds = selectedCameras.map((camera) => camera.id);
    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    console.log("Fetching media with params:", {
      selectedCameraIds,
      formattedStartDate,
      formattedEndDate,
      groupId,
      selectedIdentifiedObjects,
      cursor,
      PAGE_SIZE,
    });

    getMedias(
      selectedCameraIds,
      formattedStartDate,
      formattedEndDate,
      groupId,
      selectedIdentifiedObjects,
      cursor,
      PAGE_SIZE
    ).subscribe({
      next: (res: any) => {
        console.log("API response:", res);
        const items = res.results.map((item: MediaItem) => ({
          ...item,
        }));
        console.log("all the items", items);
        setMediaItems(items); // Replace the items
        const parseCursorFromUrl = (value: string | null) => {
          if (!value) return null;
          try {
            const parsed = new URL(value, window.location.origin);
            return parsed.searchParams.get("cursor");
          } catch {
            return null;
          }
        };
        setNextCursor(parseCursorFromUrl(res.next));
        setPreviousCursor(parseCursorFromUrl(res.previous));
        if (typeof res.total_pages === "number") {
          setTotalPages(res.total_pages);
        }
      },
      error: (error: string) => {
        console.error("Error fetching media:", error);
      },
      complete: () => setIsLoading(false),
    });
  }, [
    selectedCameras,
    startDate,
    endDate,
    specificDate,
    groupId,
    selectedIdentifiedObjects,
    cursor,
    isLoading,
  ]);
  useEffect(() => {
    setCursor(null);
    setPageIndex(1);
    setTotalPages(null);
  }, [selectedIdentifiedObjects]);

  useEffect(() => {
    // Reset media items and page when date or cameras change
    setMediaItems([]);
    setCursor(null);
    setPageIndex(1);
    setTotalPages(null);
  }, [
    selectedDate,
    selectedCameras,
    startDate,
    endDate,
    specificDate,
    groupId,
    selectedIdentifiedObjects,
  ]);

  // Trigger data load after resetting the page
  useEffect(() => {
    loadMedia();
  }, [
    cursor,
    startDate,
    endDate,
    selectedCameras,
    groupId,
    selectedIdentifiedObjects,
  ]);
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setCursor(null);
    setPageIndex(1);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setCursor(null);
    setPageIndex(1);
  };
  const handleMediaSelect = (media: MediaItem) => {
    setSelectedMedia(media);
    setSelectedItemId(media.id);
  };

  const handlePreviousPage = () => {
    if (!previousCursor) return;
    setCursor(previousCursor);
    setPageIndex((current) => Math.max(1, current - 1));
  };

  const handleNextPage = () => {
    if (!nextCursor) return;
    setCursor(nextCursor);
    setPageIndex((current) => current + 1);
  };

  return (
    <div className="mt-2">
      <IdentifiedSpecies mode={"multi"} setPage={() => {
        setCursor(null);
        setPageIndex(1);
      }} />
      <span className="d-block fw-bold mb-1">
        <small>Date Range:</small>
      </span>
      <Card
        border="light"
        className="bg-white shadow-sm rounded border-1 p-1 koala-card mt-1"
      >
        <Card.Body className="py-0">
          <Form>
            <div className="row">
              <Form.Group className="col-md-6 mb-2" controlId="start-date">
                <Form.Label>
                  <small>Start Date:</small>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  className="form-control-sm"
                  style={{ marginTop: "-5px" }}
                />
              </Form.Group>

              <Form.Group className="col-md-6 mb-2" controlId="end-date">
                <Form.Label>
                  <small>End Date:</small>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  className="form-control-sm"
                  style={{ marginTop: "-5px" }}
                />
              </Form.Group>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Conditional Spinner */}
      {isLoading ? (
        <div className="text-center my-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : mediaItems.length > 0 ? (
        <Card className="bg-white shadow-sm rounded border-1 koala-card mt-2">
          <Card.Body className="p-0">
            <ListGroup className="rounded-top">
              {mediaItems.map((media, index) => (
                <ListGroup.Item
                  key={media.id}
                  action
                  onClick={() => handleMediaSelect(media)}
                  style={{
                    backgroundColor:
                      media.id === selectedItemId ? "#f0f0f0" : "white",
                    borderBottom: "1px solid #dddd",
                    borderLeft: "none",
                    borderRight: "none",
                    borderTop: "none",
                    borderRadius: index === 0 ? "8px 8px 0 0" : "0", // Top border-radius only for the first item
                  }}
                >
                  <a>
                    <small>{media.title}</small>
                  </a>
                </ListGroup.Item>
              ))}
            </ListGroup>
            <Row className="px-2 d-flex justify-content-between align-items-center">
              <Col>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px",
                    width: "100%",
                  }}
                >
                  <div style={{ fontSize: "0.85rem" }}>
                    Page {pageIndex}
                    {totalPages ? ` / ${totalPages}` : ""}
                  </div>
                  <div>
                    <button
                      className="btn btn-sm btn-secondary me-2"
                      disabled={!previousCursor}
                      onClick={handlePreviousPage}
                    >
                      Previous
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={!nextCursor}
                      onClick={handleNextPage}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ) : (
        <div>No media available for the selected date or criteria.</div>
      )}
    </div>
  );
};
export default Media;
